import { Stack, Typography, useTheme } from "@mui/material";
import {
  useCreateKbFile,
  useCreateKbFolder,
  usePermissionStore,
} from "controller";
import { useAtomValue } from "jotai";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PERMISSIONS } from "shared";
import { centralFolderList } from "../store";
import { getDefaultArticleContent } from "../helpers";
import {
  Button,
  DropdownMenu,
  FolderPlusIcon,
  ImportIcon,
  NoteIcon,
  PlusIcon,
} from "components";
import NoData2 from "assets/images/nodata/noData2.png";
import { KbImportDrawer } from "./KbImportDrawer";
import { useState } from "react";
import { addMixpanelEvent } from "utilz/helpers";

export const KbNoDataScreen = ({
  showCreateButton = true,
}: {
  showCreateButton?: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { folderId } = useParams();
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const centralKbFolder = useAtomValue(centralFolderList);
  const { mutateAsync: createKbFolder, isLoading: isCreateKbFolderLoading } =
    useCreateKbFolder();
  const { mutateAsync: createKbFile, isLoading: isCreateKbFileLoading } =
    useCreateKbFile();
  const createOptions = [
    {
      name: "Article",
      value: "create_new_document",
      icon: <NoteIcon fontSize="small" />,
    },
    {
      name: "Folder",
      value: "create_new_folder",
      icon: <FolderPlusIcon fontSize="small" />,
    },
    {
      name: "Import",
      value: "import",
      icon: <ImportIcon fontSize="small" />,
    },
  ];

  const handleCreateNew = async ({ value }: { value: string }) => {
    try {
      switch (value) {
        case "create_new_document":
          const { content, update } = getDefaultArticleContent();
          const fileData = await createKbFile({
            name: "Untitled",
            parent_id: folderId || centralKbFolder?.[0]?.mapping_id || null,
            content_type: "JSON",
            content,
            excerpt: "",
            yjs_update: update,
          });

          addMixpanelEvent({
            event: "kb_article_created",
            data: {
              folderId,
            },
          });

          navigate(
            `/app/kb/document/${fileData?.[0]?.article_id}?mode=edit&ss_id=${fileData?.[0]?.mapping_id}`,
          );

          break;
        case "create_new_folder":
          // navigate("/app/kb/create-folder");
          await createKbFolder({
            folderName: "New Folder",
            parentId: folderId || null,
          });

          addMixpanelEvent({
            event: "kb_folder_created",
            data: {
              folderId,
            },
          });

          break;

        case "import":
          setImportDrawerOpen(true);
          break;
      }
    } catch (error) {}
  };
  return (
    <Stack
      sx={{
        p: "88px 24px",
      }}
    >
      <Stack
        sx={{
          height: "440px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: "8px",
          textAlign: "center",
          gap: "8px",
        }}
      >
        <img src={NoData2} alt="No data" width={"150px"} height={"150px"} />

        <Typography variant="h5">
          {searchParams.get("search") ? "No results found" : "It’s quiet here"}
        </Typography>
        <Typography variant="body-small" color={theme.palette.lightBg.medium}>
          {searchParams.get("search")
            ? "Your search didn’t return any articles or folders."
            : "You haven’t added any articles or folders yet."}
        </Typography>
        {haveFullAccess && showCreateButton && (
          <DropdownMenu
            sx={{
              mt: "8px",
            }}
            options={createOptions}
            onOptionClick={({ value }) => {
              handleCreateNew({ value: value as string });
            }}
          >
            <Button
              loading={isCreateKbFolderLoading || isCreateKbFileLoading}
              endIcon={<PlusIcon />}
            >
              Create
            </Button>
          </DropdownMenu>
        )}
      </Stack>
      {importDrawerOpen && (
        <KbImportDrawer
          open={importDrawerOpen}
          onClose={() => setImportDrawerOpen(false)}
          folderId={folderId}
        />
      )}
    </Stack>
  );
};
